import { useState } from "react";
import { useFormContext } from "react-hook-form";

import BeforeAfterCommands from "components/BeforeAfterCommands";
import { Commands } from "components/BeforeAfterCommands/types";
import { useObserveForWarning } from "components/WarningContext/useObserveForWarning";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Select from "ds/components/Select";
import SelectItem from "ds/components/Select/Item";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import { PhaseName, phases } from "utils/hooks";

import { ContextCreationWizardStepName } from "../constants";
import NewContextFooter from "../Footer";
import { useSaveContext } from "../hooks/useSaveContext";
import { ContextFormFields } from "../types";

const NewContextAddHooks = () => {
  const [selectedPhase, setSelectedPhase] = useState(Object.keys(phases)[0] as PhaseName);
  const { handleSubmit, setValue, watch } = useFormContext<ContextFormFields>();
  const [hasUnsubmittedChanges, setHasUnsubmittedChanges] = useState(false);

  const [beforeValue, setBeforeValue] = useState("");
  const [afterValue, setAfterValue] = useState("");

  const onBeforeInputChange = (value: string) => {
    setBeforeValue(value);
    setHasUnsubmittedChanges?.(!!(value || afterValue));
  };

  const onAfterInputChange = (value: string) => {
    setAfterValue(value);
    setHasUnsubmittedChanges?.(!!(value || beforeValue));
  };

  useObserveForWarning(
    hasUnsubmittedChanges,
    <>
      You have not added the <strong>command</strong>. Do you want to continue without adding it?
    </>
  );

  const allCommands = watch("hooks");
  const commands = allCommands[selectedPhase];

  const handleCommandsChange = (commands: Commands) => {
    if (commands.before) {
      setValue(`hooks.${selectedPhase}.before`, commands.before);
    }

    if (commands.after) {
      setValue(`hooks.${selectedPhase}.after`, commands.after);
    }
  };

  const [saveContext, isSavingContext] = useSaveContext(ContextCreationWizardStepName.Hooks);

  const phaseOptions = (Object.entries(phases) as [PhaseName, string][]).map(([name, label]) => {
    const commands = allCommands[name];
    const beforeCommandsCount = commands?.before?.length ?? 0;
    const afterCommandsCount = commands?.after?.length ?? 0;

    return {
      label,
      value: name,
      postfix: String(beforeCommandsCount + afterCommandsCount),
    };
  });

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Add hooks (optional)"
          description="Spacelift workflow can be customized by adding extra commands to be executed before and after each of the phases."
        />
        <Box direction="column" gap="medium" margin="0 0 large">
          <Select
            label="Customize workflow"
            tooltipInfo={
              <>
                <TooltipModalTitle>Customize workflow</TooltipModalTitle>
                <TooltipModalBody align="start">
                  <Typography tag="p" variant="p-body3">
                    Customize Spacelift run lifecycle by adding commands to run before and after
                    various execution phases.
                  </Typography>
                </TooltipModalBody>
              </>
            }
            value={selectedPhase}
            items={phaseOptions}
            onChange={(val: PhaseName) => setSelectedPhase(val)}
          >
            {(item) => (
              <SelectItem
                id={item.value}
                label={item.label}
                value={item.value}
                suffix={<Counter count={item.postfix} />}
              />
            )}
          </Select>
          <BeforeAfterCommands
            onAfterInputChange={onAfterInputChange}
            onBeforeInputChange={onBeforeInputChange}
            commands={commands}
            onChange={handleCommandsChange}
          />
        </Box>
      </FullScreenModalBody>
      <NewContextFooter
        onCreateContextClick={handleSubmit(saveContext)}
        isCreatingContext={isSavingContext}
      />
    </>
  );
};

export default NewContextAddHooks;
